import { Link } from "react-router-dom";
import { Layers, EyeOff, Link2 } from "lucide-react";
import { assetUrl } from "@/lib/api";
import { DFC_LAYOUTS, RARITY_CLS } from "@/lib/mtg";
import { ManaCost } from "@/components/common/ManaCost";
import { VisibilityBadge } from "@/components/common/Badges";

export function CardTile({ card, index = 0, showOwner = true }) {
  const dfc = DFC_LAYOUTS.includes(card.layout);
  return (
    <Link
      to={`/cards/${card.card_id}`}
      className="group rise block focus-visible:outline-none"
      style={{ animationDelay: `${Math.min(index, 12) * 40}ms` }}
      data-testid={`card-tile-${card.card_id}`}
    >
      <div className="card-frame shimmer group-focus-visible:ring-2 group-focus-visible:ring-amber-400" style={{ aspectRatio: card.dimensions?.aspect_ratio || 63 / 88 }}>
        <img src={assetUrl(card.image_url)} alt={card.name} loading="lazy" className={`h-full w-full ${card.design?.source === "external" ? "object-contain" : "object-cover"}`} />
        <div className="absolute left-2 top-2 flex gap-1">
          {dfc && (
            <span className="inline-flex items-center gap-1 rounded bg-black/70 px-1.5 py-0.5 font-mono text-[10px] uppercase text-amber-300" title="Double-faced">
              <Layers className="h-3 w-3" aria-hidden />DFC
            </span>
          )}
          {card.layout === "token" && <span className="rounded bg-black/70 px-1.5 py-0.5 font-mono text-[10px] uppercase text-cyan-300">Token</span>}
        </div>
        {card.visibility !== "public" && (
          <div className="absolute right-2 top-2 flex items-center gap-1">
            {card.visibility === "private" ? <EyeOff className="h-3 w-3 text-rose-300" aria-hidden /> : <Link2 className="h-3 w-3 text-amber-300" aria-hidden />}
            <VisibilityBadge visibility={card.visibility} />
          </div>
        )}
      </div>
      <div className="mt-2.5 space-y-1 px-0.5">
        <div className="flex items-start justify-between gap-2">
          <p className="line-clamp-1 text-sm font-semibold text-slate-100 group-hover:text-amber-200">{card.name}</p>
          <ManaCost cost={card.faces[0]?.mana_cost} />
        </div>
        <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
          <span className="line-clamp-1">{showOwner && card.owner ? `by @${card.owner.username}` : card.type_line}</span>
          <span className={`shrink-0 rounded border px-1 font-mono text-[9px] uppercase ${RARITY_CLS[card.rarity]}`}>{card.rarity[0]}</span>
        </div>
      </div>
    </Link>
  );
}

export function CardGrid({ cards, showOwner = true, testid = "card-grid" }) {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-6 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6" data-testid={testid}>
      {cards.map((c, i) => <CardTile key={c.card_id} card={c} index={i} showOwner={showOwner} />)}
    </div>
  );
}

export function CardGridSkeleton({ count = 12 }) {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-6 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6" data-testid="card-grid-loading">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="aspect-[63/88] animate-pulse rounded-xl border border-[#2A3447] bg-[#121620]" />
      ))}
    </div>
  );
}
